import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.webp'

const ReceiptModal = ({ isOpen, onClose, paymentData, customerName }) => {
  const [customerInfo, setCustomerInfo] = useState(customerName || 'Customer');

  useEffect(() => {
    if (customerName) {
      setCustomerInfo(customerName);
    }
  }, [customerName]);

  // Directly trigger print without showing modal
  useEffect(() => {
    if (isOpen && paymentData) {
      handlePrint();
    }
  }, [isOpen, paymentData]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const generateReceiptNumber = () => {
    // Use payment_id from API response if available
    if (paymentData?.payment_id) {
      return paymentData.payment_id;
    }
    // Fallback to customer registration number if available
    if (paymentData?.customer_info?.registration_no) {
      return paymentData.customer_info.registration_no;
    }
    // Final fallback to payment ID
    return `BLG${String(paymentData.id).padStart(3, '0')}`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    const receiptContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Money Receipt - ${generateReceiptNumber()}</title>
          <style>
              body {
                  font-family: 'Arial', sans-serif;
                  margin: 0;
                  padding: 0;
                  background-color: #f4f4f4;
              }
              .receipt-container {
                  width: 190mm;
                  min-height: 148mm;
                  margin: 20px auto;
                  background: #fff;
                  padding: 15mm;
                  box-sizing: border-box;
                  box-shadow: 0 0 10px rgba(0,0,0,0.1);
                  position: relative;
                  border: 2px solid #000;
              }
              .header {
                  text-align: center;
                  border-bottom: 2px solid #000;
                  padding-bottom: 10px;
                  margin-bottom: 20px;
              }
              .logo {
                  width: 70px;
                  position: absolute;
                  left: 20mm;
                  top: 15mm;
              }
              .title {
                  font-size: 18px;
                  font-weight: bold;
                  text-transform: uppercase;
              }
              .receipt-label {
                  display: inline-block;
                  background: #000;
                  color: #fff;
                  padding: 5px 15px;
                  margin-top: 10px;
                  font-weight: bold;
                  text-align: center;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
              }
              .info-section {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 30px;
              }
              .content-body {
                  margin-top: 20px;
              }
              .row {
                  margin: 15px 0;
                  display: flex;
                  align-items: baseline;
              }
              .label {
                  font-weight: bold;
                  min-width: 180px;
              }
              .value {
                  border-bottom: 1px dotted #000;
                  flex-grow: 1;
                  padding-left: 10px;
              }
              .footer {
                  margin-top: 50px;
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-end;
              }
              .sig-box {
                  text-align: center;
              }
              .sig-line {
                  border-top: 1px solid #000;
                  width: 200px;
                  margin-bottom: 5px;
              }
              .print-btn {
                  position: fixed;
                  bottom: 20px;
                  right: 20px;
                  padding: 10px 20px;
                  background-color: #28a745;
                  color: #fff;
                  border: none;
                  border-radius: 5px;
                  cursor: pointer;
                  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              }
              @media print {
                  .print-btn { display: none; }
                  body { 
                      background: none; 
                      margin: 0;
                      padding: 0;
                  }
                  .receipt-container { 
                      width: 190mm;
                      margin: 0 auto; 
                      box-shadow: none; 
                      border: 2px solid #000; 
                  }
                  @page { 
                      size: A4 landscape; 
                      margin: 10mm; 
                  }
              }
          </style>
      </head>
      <body>

          <button class="print-btn" onclick="window.print()">Print Receipt</button>

          <div class="receipt-container">
              <img src=${logo} class="logo" alt="logo">
              <div class="header">
                  <div class="title">BALURGHAT MUNICIPALITY SWIMMING POOL</div>
                  <div>BALURGHAT, DAKSHIN DINAJPUR</div>
                  <div class="receipt-label">MONEY RECEIPT</div>
              </div>

              <div class="info-section">
                  <div><strong>Trans ID:</strong> ${generateReceiptNumber()}</div>
                  <div><strong>Date:</strong> ${formatDate(paymentData.created_at)}</div>
              </div>

              <div class="content-body">
                  <div class="row">
                      <span class="label">Received with thanks from:</span>
                      <span class="value">${customerInfo.toUpperCase()}</span>
                  </div>
                  
                  <div class="row">
                      <span class="label">On account of (Category):</span>
                      <span class="value">${paymentData.category?.category || 'N/A'}</span>
                  </div>

                   <div class="row">
                      <span class="label">Selected Session:</span>
                      <span class="value">${paymentData.session?.session || 'N/A'}</span>
                  </div>

                  <div class="row" style="display: flex; justify-content: space-between;">
                      <div style="display: flex; align-items: baseline; flex: 1;">
                          <span class="label" style="min-width: 100px;">Shift:</span>
                          <span class="value">${paymentData.shift?.shift_name || 'N/A'}</span>
                      </div>
                      <div style="display: flex; align-items: baseline; flex: 1; padding-left: 20px;">
                          <span class="label" style="min-width: 100px;">Time Slot:</span>
                          <span class="value">${paymentData.time_slot?.time || 'N/A'}</span>
                      </div>
                  </div>

                  <div class="row">
                      <span class="label">The sum of Rupees:</span>
                      <span class="value">₹ ${parseFloat(paymentData.amount || 0).toFixed(2)}</span>
                  </div>
                  
                  <div class="row">
                      <span class="label">Payment Method:</span>
                      <span class="value">${paymentData.payment_type || 'N/A'}</span>
                  </div>
              </div>

              <div class="footer">
                  <div class="amount-box" style="border: 2px solid #000; padding: 10px 20px; font-weight: bold; font-size: 18px;">
                      Rs. ${parseFloat(paymentData.amount || 0).toFixed(2)}/-
                  </div>
                  <div class="sig-box">
                      <div class="sig-line"></div>
                      <div>Authority Signatory</div>
                  </div>
              </div>
          </div>

      </body>
      </html>
    `;

    printWindow.document.write(receiptContent);
    printWindow.document.close();
    
    // Wait for the content to load, then print
    printWindow.onload = function() {
      printWindow.print();
      printWindow.close();
    };

    // Close the modal immediately after opening print window
    onClose();
  };

  return null; // Don't render anything, just trigger print
};

export default ReceiptModal;
